/**
 * Murphy Door Configurator - Auto-Navigation Coordinator
 *
 * PURPOSE: Event-driven auto-navigation that guides users to incomplete
 * configuration steps after fresh loads, URL restoration, and selection applies.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - Event-driven coordination ONLY (no direct module calls)
 * - PPD-First data access via conductor delegation
 * - Publishes ui:* events for all navigation requests (CONSTANTS.EVENTS.*)
 * - 5-stage event lifecycle for all operations
 * - Bootstrap barrier pattern for race protection
 * - CoreEventBus central freeze (Object.freeze() DRY via Pattern #18)
 * - Performance tracking with configurable thresholds
 * - Comprehensive cleanup for all event subscriptions
 *
 * RESPONSIBILITIES:
 * 1. Subscribe to system:initialization_complete for fresh page loads
 * 2. Subscribe to restoration:complete for URL parameter restoration
 * 3. Subscribe to state:progress_updated for post-selection navigation (NOT selection:applied - see timing fix)
 * 4. Track manual user interactions (tab/category clicks) to disable auto-nav
 * 5. Determine navigation target via conductor delegation (PPD-first)
 * 6. Publish ui:tab_open_request / ui:category_open_request events
 * 7. Publish complete 5-stage lifecycle events for monitoring
 * 8. DEFER navigation during cascade auto-selection to prevent navigating TO auto-applied categories
 *    - Subscribe to business:cascade_auto_selection_started (defer navigation)
 *    - Subscribe to business:cascade_auto_selection_step (track auto-applied categories)
 *    - Subscribe to business:cascade_auto_selection_completed (execute deferred navigation)
 *
 * DATA FLOW:
 * system:initialization_complete → handleFreshLoad() → determineTargetNavigation()
 *   → conductor.calculateTotalProgress() / conductor.findFirstIncompleteTab()
 *   → triggerAutoNavigation() → ui:tab_open_request + ui:category_open_request
 *
 * @version 1.0.0
 * @author Murphy Door Configurator Team
 */
(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // ============================================================================
  // DEV-ONLY LOGGING HELPER
  // ============================================================================
  /**
   * Check if running in development environment
   * @returns {boolean} True if in development mode
   */
  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1: GLOBAL CONSTANTS USAGE (10 points)
  // ============================================================================
  /**
   * Using global CONSTANTS from md-config-core-constants.js
   * This eliminates magic strings and ensures consistency across modules.
   * All event names are defined once in the global constants file.
   */

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    NAVIGATION_DETERMINATION: 50, // 50ms for determining target
    NAVIGATION_TRIGGER: 100, // 100ms for full navigation trigger
    TOTAL_OPERATION: 150, // 150ms for complete auto-nav cycle
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // LOCAL COORDINATION STATE (CCP-compliant flags)
  // ============================================================================
  /**
   * Tracks whether the user has manually clicked a tab or category.
   * Once true, auto-navigation is permanently disabled for this session.
   * This prevents fighting with user intent.
   */
  let userHasManuallyInteracted = false;

  /**
   * Tracks whether auto-navigation has already fired for the current context.
   * Reset on restoration:complete and selection:applied to allow re-navigation.
   * Prevents duplicate firing within the same trigger event.
   */
  let autoNavigationFired = false;

  /**
   * Tracks whether a cascade auto-selection is currently in progress.
   * When true, auto-navigation defers until cascade completes.
   * This prevents navigating TO a category that is about to be auto-applied.
   */
  let cascadeInProgress = false;

  /**
   * Set of categoryIds that were auto-applied by the cascade coordinator.
   * Auto-navigation will SKIP navigating to these categories.
   * Cleared when cascade completes and deferred navigation executes.
   */
  const cascadeAutoAppliedCategories = new Set();

  /**
   * Suppresses exactly one state:progress_updated-triggered navigation after
   * an unapply completes. The unapply handler navigates precisely to the
   * unapplied category; the subsequent progress_updated would cause a
   * redundant (and potentially wrong) second navigation.
   *
   * LIFECYCLE:
   * 1. SET to true  — in handleUnapplyCompleted(), after triggerAutoNavigation() succeeds
   * 2. CONSUMED (reset to false) — in handleSelectionApplied(), ignoring the next
   *    STATE_PROGRESS_UPDATED navigation so it doesn't overwrite the precise unapply nav
   * 3. SAFETY RESET — in SELECTION_USER_UNAPPLY_REQUESTED handler, clears any stale
   *    true value if the unapply process aborts before COMPLETED fires
   * 4. CLEANUP RESET — in cleanup(), set back to false on full module teardown
   */
  let suppressProgressNavigation = false;

  /**
   * Deferred navigation timer. When state:progress_updated fires,
   * navigation is deferred by a short window to allow cascade auto-selection
   * to claim categories first. If a cascade starts during this window,
   * navigation waits until cascade completes.
   */
  let deferredNavigationTimer = null;

  /**
   * Stored conductor reference for deferred navigation execution.
   */
  let deferredConductorRef = null;

  /**
   * True while simplified mode is active (3-question FMD2 flow).
   * Auto-navigation must be suppressed until the user completes the simplified
   * flow and BUSINESS_SIMPLIFIED_MODE_COMPLETE fires — otherwise the fresh-load
   * auto-navigator immediately overrides the My Door tab activation.
   */
  let simplifiedModeActive = false;

  // ============================================================================
  // MODULE REFERENCES
  // ============================================================================
  let eventBus = null;

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION)
  // ============================================================================
  if (CoreEventBus) {
    // PATTERN 15: Track bootstrap subscription for cleanup
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] Bootstrap complete event received'
          );
        bootstrapComplete = true;
        eventBus = CoreEventBus;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[AutoNavigationCoordinator] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[AutoNavigationCoordinator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[AutoNavigationCoordinator] Subscribed to bootstrap event at module load time'
      );

    // ✅ CCP GOLDEN STANDARD: Module-load-time subscription with bootstrap guard
    // FIX: Publish-before-subscribe race condition identified by ccp-cascade-logic-auditor.
    // UIUpdater attaches the disabled button click handler EARLY (on Summary tab activation),
    // but initialize() runs LATE during conductor bootstrap. Moving this subscription to
    // module load time ensures the subscriber exists when the event fires.
    // Same pattern as UIUpdater's UI_SUMMARY_TAB_ACTIVATED subscription (ui-updater.js:4098)
    const unsubDisabledCart = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED,
      (eventData) => {
        // Bootstrap guard: wait for conductor to be available
        if (!bootstrapComplete) {
          isDev() &&
            console.log(
              '[AutoNavigationCoordinator] UI_ADD_TO_CART_DISABLED_CLICKED received before bootstrap, queueing'
            );
          pendingCalls.push({
            fn: handleDisabledCartButtonClick,
            args: [eventData],
          });
          return;
        }

        // Conductor ready - handle immediately
        handleDisabledCartButtonClick(eventData);
      }
    );
    cleanupHandlers.push(unsubDisabledCart);

    isDev() &&
      console.log(
        '[AutoNavigationCoordinator] Subscribed to UI_ADD_TO_CART_DISABLED_CLICKED at module load time'
      );

    // Suppress fresh-load auto-navigation during simplified mode (3-question FMD2 flow).
    // BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY fires during bootstrap (before SYSTEM_INITIALIZATION_COMPLETE),
    // so a module-load-time subscription here ensures the flag is set before handleFreshLoad runs.
    const unsubSimplifiedActive = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY,
      () => {
        simplifiedModeActive = true;
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] Simplified mode active — auto-navigation suppressed'
          );
      }
    );
    cleanupHandlers.push(unsubSimplifiedActive);

    const unsubSimplifiedComplete = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_COMPLETE,
      () => {
        simplifiedModeActive = false;
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] Simplified mode complete — auto-navigation re-enabled'
          );
      }
    );
    cleanupHandlers.push(unsubSimplifiedComplete);

    // Subscribe to close icon click: collapse current category and navigate to next incomplete
    const unsubCloseIcon = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_CLOSE_CATEGORY_ICON_CLICKED,
      (eventData) => {
        if (!bootstrapComplete) return;
        const conductor = window.MurphyDoorConfigurator?.conductor;
        if (!conductor) return;
        const { categoryId } = eventData || {};
        const categoriesToSkip = categoryId ? new Set([categoryId]) : new Set();
        _handleNavigationTrigger(
          conductor,
          'close_icon_clicked',
          categoriesToSkip
        );
      }
    );
    cleanupHandlers.push(unsubCloseIcon);

    isDev() &&
      console.log(
        '[AutoNavigationCoordinator] Subscribed to UI_CLOSE_CATEGORY_ICON_CLICKED at module load time'
      );
  }

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER (10 points)
  // ============================================================================
  /**
   * Centralized event publishing with error handling.
   *
   * PATTERN 11: Async Event Publishing
   * PATTERN 18: CoreEventBus.publish() freezes ALL payloads centrally (DRY)
   *             - No Object.freeze() needed here; CoreEventBus handles it.
   *
   * @param {string} eventType - Event type constant from CONSTANTS.EVENTS
   * @param {Object} detail - Event payload data
   * @param {string} [context='operation'] - Operation context for debugging
   * @returns {boolean} Success status
   */
  function publishEvent(eventType, detail, context = 'operation') {
    const bus = eventBus || window.MurphyDoorConfigurator?.CoreEventBus;

    if (!bus) {
      isDev() &&
        console.warn(
          `[AutoNavigationCoordinator] EventBus not available, cannot publish: ${eventType}`
        );
      return false;
    }

    try {
      // PATTERN 18: No Object.freeze() needed - CoreEventBus.publish() freezes centrally (DRY)
      const enrichedDetail = {
        ...detail,
        timestamp: detail.timestamp || Date.now(),
        source: detail.source || 'AutoNavigationCoordinator',
      };

      const success = bus.publish(eventType, enrichedDetail);

      if (!success) {
        isDev() &&
          console.warn(
            `[AutoNavigationCoordinator] Event publication returned false: ${eventType}`,
            { context }
          );
      }

      return success;
    } catch (error) {
      isDev() &&
        console.error(
          `[AutoNavigationCoordinator] Failed to publish event ${eventType}:`,
          error
        );

      // Attempt to publish failure event (avoid recursion on FAILED events)
      if (
        eventType !== CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED &&
        bus
      ) {
        try {
          // PATTERN 18: No Object.freeze() needed - CoreEventBus.publish() freezes centrally (DRY)
          bus.publish(CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED, {
            error: error.message,
            context: context,
            originalEvent: eventType,
            timestamp: Date.now(),
            source: 'AutoNavigationCoordinator',
          });
        } catch (nestedError) {
          isDev() &&
            console.error(
              '[AutoNavigationCoordinator] Failed to publish failure event:',
              nestedError
            );
        }
      }

      return false;
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Track operation performance and publish slow operation events.
   *
   * @param {string} operationType - Type of operation being tracked
   * @param {number} duration - Duration in milliseconds
   * @param {Object} [metadata={}] - Additional metadata for the performance event
   */
  function trackPerformance(operationType, duration, metadata = {}) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];

    if (threshold && duration > threshold) {
      publishEvent(
        CONSTANTS.EVENTS.PERFORMANCE_AUTO_NAVIGATION_SLOW,
        {
          operation: operationType,
          duration: parseFloat(duration.toFixed(2)),
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
          ...metadata,
        },
        'performance_tracking'
      );

      isDev() &&
        console.warn(
          `[AutoNavigationCoordinator] PERFORMANCE: ${operationType} took ${duration.toFixed(2)}ms ` +
            `(threshold: ${threshold}ms)`
        );
    }
  }

  // ============================================================================
  // CORE BUSINESS LOGIC: determineTargetNavigation
  // ============================================================================
  /**
   * Determines where auto-navigation should go next.
   *
   * PATTERN 7: Perfect Dictionary Only (PPD-First via conductor delegation)
   * PATTERN 8: No Raw Data Access (all data through conductor)
   * PATTERN 9: Data Request via conductor delegation
   *
   * @param {Object} conductor - The central conductor for delegation
   * @returns {{ tabId: string, categoryId?: string|null, reason: string }|null}
   *          Navigation target or null if no navigation needed
   */
  /**
   * Gate cards (e.g. "Cabinets?") hide their dependent real categories until
   * answered -- but those categories still exist in sortedCategories (they're
   * real, PPD-derived categories), so findFirstIncompleteCategory() has no
   * way to know they're currently gate-hidden and would otherwise land the
   * user directly on a category the gate is supposed to keep hidden,
   * bypassing the question entirely. Adds any currently-hidden dependent
   * category to the existing categoriesToSkip mechanism (already used for
   * cascade-auto-applied categories) so auto-nav never targets one.
   * @param {Object} conductor
   * @returns {Set<string>}
   */
  function _computeGateHiddenCategories(conductor) {
    const hidden = new Set();
    try {
      if (!conductor?._areGateCardsEnabled?.()) return hidden;
      const gateDeps =
        conductor._getGateVisibilityDeps?.() ||
        CONSTANTS.CATEGORY_VISIBILITY_DEPS;
      if (!gateDeps) return hidden;
      Object.keys(gateDeps).forEach((gateId) => {
        const gateConfig = gateDeps[gateId];
        const answer = conductor._getGateAnswer
          ? conductor._getGateAnswer(gateId)
          : null;
        const visibleCategories =
          answer && gateConfig.answers[answer]
            ? gateConfig.answers[answer]
            : [];
        gateConfig.categories.forEach((categoryName) => {
          if (visibleCategories.indexOf(categoryName) === -1) {
            hidden.add(categoryName);
          }
        });
      });
    } catch (error) {
      isDev() &&
        console.warn(
          '[AutoNavigationCoordinator] _computeGateHiddenCategories failed:',
          error
        );
    }
    return hidden;
  }

  /**
   * Second progressive-disclosure layer (e.g. Top Knobs & Pulls hidden until
   * Top Cabinet Doors has a REAL applied style, not the gate's auto-applied
   * "no doors" fallback). Same problem as _computeGateHiddenCategories above
   * -- without this, auto-nav would land directly on a category this layer
   * is supposed to keep hidden the instant the gate-hidden layer above it is
   * skipped. Confirmed live: skipping only the gate layer surfaced this
   * exact gap (auto-nav landed on "Top Knobs & Pulls" instead of stopping at
   * the still-unanswered "Cabinets?" gate).
   * @param {Object} conductor
   * @returns {Set<string>}
   */
  function _computeSelectionHiddenCategories(conductor) {
    const hidden = new Set();
    try {
      if (!conductor?._areGateCardsEnabled?.()) return hidden;
      // Via the conductor so an admin selection_dependency_rule and the
      // constant can never disagree between this and the three other sites
      // that read the same map.
      const deps =
        conductor._getSelectionVisibilityDeps?.() ??
        CONSTANTS.SELECTION_VISIBILITY_DEPS;
      if (!deps || !conductor.getAppliedSelectionsMap) return hidden;
      const appliedMap = conductor.getAppliedSelectionsMap();
      Object.keys(deps).forEach((dependentCategory) => {
        const dep = deps[dependentCategory];
        const parentSelection = appliedMap
          ? appliedMap.get(dep.dependsOnCategory)
          : null;
        let isRealStyleChosen = false;
        if (parentSelection && parentSelection.productId) {
          const products = conductor.getVirtualizedProductsForCategory
            ? conductor.getVirtualizedProductsForCategory(
                dep.dependsOnCategory
              ) || []
            : [];
          const product = products.find(
            (p) => p && p.id === parentSelection.productId
          );
          isRealStyleChosen = !!(
            product && product.handle !== dep.hiddenWhenProductHandle
          );
        }
        if (!isRealStyleChosen) {
          hidden.add(dependentCategory);
        }
      });
    } catch (error) {
      isDev() &&
        console.warn(
          '[AutoNavigationCoordinator] _computeSelectionHiddenCategories failed:',
          error
        );
    }
    return hidden;
  }

  /**
   * Daniel's spec: the gate itself is a stop in the sequence, not something
   * auto-nav silently walks past on its way to the next real category. If
   * the category auto-nav would land on next (computed WITHOUT gate/selection
   * hiding applied) is currently hidden behind an unanswered gate, target the
   * gate card instead of skipping straight through to whatever real category
   * comes after it.
   * @param {Object} conductor
   * @param {string} tabId
   * @param {Set<string>} originalSkip - caller-supplied skip set (cascade-applied only)
   * @param {Set<string>} gateHidden - from _computeGateHiddenCategories()
   * @returns {{tabId: string, categoryId: null, gateId: string, reason: string}|null}
   */
  function _findNextUnansweredGateTarget(
    conductor,
    tabId,
    originalSkip,
    gateHidden
  ) {
    try {
      if (!conductor?._areGateCardsEnabled?.() || gateHidden.size === 0) {
        return null;
      }
      const naturalNext = conductor.findFirstIncompleteCategory
        ? conductor.findFirstIncompleteCategory(tabId, originalSkip)
        : null;
      if (!naturalNext || !gateHidden.has(naturalNext)) {
        return null;
      }
      const gateDeps =
        conductor._getGateVisibilityDeps?.() ||
        CONSTANTS.CATEGORY_VISIBILITY_DEPS;
      if (!gateDeps) return null;
      const gateId = Object.keys(gateDeps).find((id) => {
        const answer = conductor._getGateAnswer
          ? conductor._getGateAnswer(id)
          : null;
        return !answer && gateDeps[id].categories.indexOf(naturalNext) !== -1;
      });
      if (!gateId) return null;
      return Object.freeze({
        tabId,
        categoryId: null,
        gateId,
        reason: 'gate_unanswered',
      });
    } catch (error) {
      isDev() &&
        console.warn(
          '[AutoNavigationCoordinator] _findNextUnansweredGateTarget failed:',
          error
        );
      return null;
    }
  }

  /**
   * apo_style renders one continuous scroll re-sorted by
   * APO_STYLE_1TO1_CATEGORY_ORDER / APO_STYLE_DEFAULT_CATEGORY_ORDER
   * (md-config-core-constants.js) -- NOT by the admin tab/loadOrder sequence
   * findFirstIncompleteTab()/findFirstIncompleteCategory() below still use.
   * Without this, auto-nav lands on whatever's next in the ORIGINAL tab
   * order (e.g. "Front Casing" right after "Wood & Finish", since both live
   * in the same admin-configured Appearance tab) even though the categories
   * in between were visually moved elsewhere by the apo_style reorder --
   * confirmed live by Daniel 2026-08-12. This walks the SAME flattened
   * sequence the DOM reveal-all pass uses, so "jump to next" always matches
   * whatever's visually next, no matter which order constant is active.
   * @param {Object} conductor
   * @param {Set<string>} categoriesToSkip - cascade-applied + gate/selection-hidden
   * @returns {{tabId: string|null, categoryId: string|null, gateId?: string, reason: string}|null}
   *          null when apo_style isn't active, or when nothing incomplete is found
   *          (caller falls through to the tab-based logic, which reaches the
   *          same "nothing incomplete" conclusion either way).
   */
  function _findApoStyleTarget(conductor, categoriesToSkip) {
    try {
      if (
        !conductor?._getConfiguratorExperience ||
        conductor._getConfiguratorExperience() !== 'apo_style'
      ) {
        return null;
      }
      const apoOrder = conductor._isApo1to1Enabled?.()
        ? CONSTANTS.APO_STYLE_1TO1_CATEGORY_ORDER
        : CONSTANTS.APO_STYLE_DEFAULT_CATEGORY_ORDER;
      if (!Array.isArray(apoOrder) || apoOrder.length === 0) {
        return null;
      }

      const sortedCategories = conductor.getSortedCategories
        ? conductor.getSortedCategories()
        : [];
      const realCategoryIds = new Set(
        sortedCategories.map(([categoryId]) => categoryId)
      );

      // Walk apoOrder first (gate slots + whichever of its category entries
      // actually exist for this product -- synonym entries for a name this
      // product doesn't use are simply absent from realCategoryIds and
      // filtered out), then any real category apoOrder doesn't know about,
      // appended in its existing sortedCategories order -- mirrors the DOM
      // reveal-all fallback so nothing is ever silently unreachable.
      const walkList = apoOrder.filter(
        (entry) => entry.indexOf('gate:') === 0 || realCategoryIds.has(entry)
      );
      const coveredReal = new Set(
        walkList.filter((entry) => entry.indexOf('gate:') !== 0)
      );
      sortedCategories.forEach(([categoryId]) => {
        if (!coveredReal.has(categoryId)) walkList.push(categoryId);
      });

      // Re-rank through the SAME resolver the DOM reveal uses
      // (md-config-ui-navigation-manager.js), so an admin-set apo_load_order
      // moves a card and its walk position together. Without this the walk
      // would keep the constant's order while the cards rendered in the
      // metafield's, and auto-navigation would start jumping backwards up the
      // page. Stable: equal ranks keep the order established above.
      if (typeof conductor._getApoOrderRank === 'function') {
        walkList
          .map((entry, originalIndex) => ({ entry, originalIndex }))
          .sort((a, b) => {
            const aRank = conductor._getApoOrderRank(a.entry);
            const bRank = conductor._getApoOrderRank(b.entry);
            if (aRank !== bRank) return aRank - bRank;
            return a.originalIndex - b.originalIndex;
          })
          .forEach((item, i) => {
            walkList[i] = item.entry;
          });
      }

      for (const entry of walkList) {
        if (entry.indexOf('gate:') === 0) {
          const gateId = entry.slice('gate:'.length);
          const gateDef = CONSTANTS.GATE_CARD_DEFINITIONS
            ? CONSTANTS.GATE_CARD_DEFINITIONS.find((g) => g.id === gateId)
            : null;
          if (!gateDef) continue; // e.g. 'gate:side' -- not built yet, ships dark
          const answer = conductor._getGateAnswer
            ? conductor._getGateAnswer(gateId)
            : null;
          if (!answer) {
            return Object.freeze({
              tabId: conductor.getParentTabForCategory
                ? conductor.getParentTabForCategory(
                    gateDef.insertBeforeCategory
                  )
                : null,
              categoryId: null,
              gateId,
              reason: 'gate_unanswered',
            });
          }
          continue;
        }

        if (categoriesToSkip.has(entry)) continue;

        const isComplete = conductor.checkCompletionStatus
          ? conductor.checkCompletionStatus(entry, 'category')
          : true;
        if (!isComplete) {
          return Object.freeze({
            tabId: conductor.getParentTabForCategory
              ? conductor.getParentTabForCategory(entry)
              : null,
            categoryId: entry,
            reason: 'first_incomplete',
          });
        }
      }

      return null;
    } catch (error) {
      isDev() &&
        console.warn(
          '[AutoNavigationCoordinator] _findApoStyleTarget failed:',
          error
        );
      return null;
    }
  }

  function determineTargetNavigation(conductor, categoriesToSkip) {
    // Union into a NEW Set rather than mutating the caller's -- callers may
    // hold onto their Set reference after this call returns.
    const originalSkip = new Set(categoriesToSkip || []);
    const gateHidden = _computeGateHiddenCategories(conductor);
    const selectionHidden = _computeSelectionHiddenCategories(conductor);
    categoriesToSkip = new Set(originalSkip);
    gateHidden.forEach((categoryId) => categoriesToSkip.add(categoryId));
    selectionHidden.forEach((categoryId) => categoriesToSkip.add(categoryId));
    const startTime = window.performance
      ? window.performance.now()
      : Date.now();

    try {
      // STEP 1: Check total progress via conductor -> progressTracker delegation
      let totalProgress = 0;
      try {
        totalProgress = conductor.calculateTotalProgress();
      } catch (progressError) {
        isDev() &&
          console.warn(
            '[AutoNavigationCoordinator] calculateTotalProgress() failed, assuming 0%:',
            progressError.message
          );
      }

      // STEP 1.5: apo_style walks its own flattened, re-sorted category
      // sequence instead of the tab-then-category-within-tab logic below --
      // see _findApoStyleTarget's doc comment. No-op (returns null) for
      // classic/dual_* or when apo_style has nothing incomplete left.
      //
      // MUST run before the totalProgress===100 short-circuit below (moved
      // here 2026-08-13, was previously after it). Gate answers (swing/hinge)
      // aren't tracked in the PPD's static totalCategoryCount denominator, so
      // calculateTotalProgress() can read 100% while a gate is still
      // unanswered -- e.g. every measurement category answered via URL
      // restoration, gates untouched. The old order let that stale 100%
      // short-circuit straight to Summary (hiding the whole categories list)
      // before this gate-aware check ever ran, permanently blocking the
      // customer with no visible next step. Confirmed live 2026-08-13.
      const apoTarget = _findApoStyleTarget(conductor, categoriesToSkip);
      if (apoTarget) {
        const apoDuration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;
        trackPerformance('NAVIGATION_DETERMINATION', apoDuration, {
          reason: apoTarget.reason,
          tabId: apoTarget.tabId,
          categoryId: apoTarget.categoryId,
          gateId: apoTarget.gateId,
        });
        return Object.freeze({ ...apoTarget, totalProgress });
      }

      // 🚨 apo_style HAS NO SUMMARY TO NAVIGATE TO (2026-08-27).
      //
      // Ryan: "When the user reaches 100%, we keep them in the view of the
      // category buttons that they've used, and the disabled add to cart button
      // there becomes enabled and usable. Don't do the current thing where
      // they're now shown nothing with an add to cart button that works."
      //
      // The summary panel is display:none in this mode and the Add to Cart
      // button now lives at the end of the category rail, so "go to summary"
      // resolves to a destination that cannot be seen -- and worse, getting
      // there runs updateTabDisplay('summary'), which sets
      // .murphy-door-configurator__categories to display:none. Both panels
      // hidden at once is the blank screen Ryan is describing; reproduced live
      // before this guard (categories display:none, summary display:none, the
      // rail button still sitting there enabled).
      //
      // Returning null means "no navigation" -- exactly right here. Completing
      // the last card should leave the customer looking at the card they just
      // answered, with the button at the bottom of the same column now live.
      //
      // The block immediately above already documents the sibling hazard: a
      // STALE 100% short-circuiting to Summary and hiding the list. This is the
      // same failure from the other direction -- a GENUINE 100% -- which was
      // harmless only while the summary was still a real place to be.
      if (
        totalProgress === 100 &&
        conductor?._getConfiguratorExperience?.() === 'apo_style'
      ) {
        trackPerformance(
          'NAVIGATION_DETERMINATION',
          (window.performance ? window.performance.now() : Date.now()) -
            startTime,
          { reason: 'apo_complete_no_navigation' }
        );
        return null;
      }

      // If 100% complete, navigate to summary tab
      if (totalProgress === 100) {
        const duration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;
        trackPerformance('NAVIGATION_DETERMINATION', duration, {
          reason: 'all_complete',
        });

        return Object.freeze({
          tabId: 'summary',
          categoryId: null,
          reason: 'all_complete',
          totalProgress,
        });
      }

      // STEP 2: Find first incomplete tab via conductor -> stateManager delegation
      let incompleteTab = null;
      try {
        incompleteTab = conductor.findFirstIncompleteTab();
      } catch (tabError) {
        isDev() &&
          console.warn(
            '[AutoNavigationCoordinator] findFirstIncompleteTab() failed:',
            tabError.message
          );
      }

      // If no incomplete tab found, navigate to summary (edge case)
      if (!incompleteTab) {
        const duration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;
        trackPerformance('NAVIGATION_DETERMINATION', duration, {
          reason: 'no_incomplete_found',
        });

        return Object.freeze({
          tabId: 'summary',
          categoryId: null,
          reason: 'no_incomplete_found',
          totalProgress,
        });
      }

      const [tabId] = incompleteTab;

      // STEP 2.5: If the next real category is hidden behind an unanswered
      // gate, stop AT the gate instead of skipping past it to whatever real
      // category comes next.
      const gateTarget = _findNextUnansweredGateTarget(
        conductor,
        tabId,
        originalSkip,
        gateHidden
      );
      if (gateTarget) {
        const gateDuration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;
        trackPerformance('NAVIGATION_DETERMINATION', gateDuration, {
          reason: 'gate_unanswered',
          tabId,
          gateId: gateTarget.gateId,
        });
        return gateTarget;
      }

      // STEP 3: Find first incomplete category in that tab, skipping auto-applied categories
      let categoryId = null;
      try {
        categoryId = conductor.findFirstIncompleteCategory(
          tabId,
          categoriesToSkip
        );
      } catch (categoryError) {
        isDev() &&
          console.warn(
            '[AutoNavigationCoordinator] findFirstIncompleteCategory() failed:',
            categoryError.message
          );
        // Still navigate to tab even if category lookup fails
      }

      const duration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;
      trackPerformance('NAVIGATION_DETERMINATION', duration, {
        reason: 'first_incomplete',
        tabId,
        categoryId,
        skippedCategories: Array.from(categoriesToSkip),
      });

      return Object.freeze({
        tabId,
        categoryId,
        reason: 'first_incomplete',
        totalProgress,
      });
    } catch (error) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] determineTargetNavigation failed:',
          error
        );
      return null;
    }
  }

  // ============================================================================
  // CORE BUSINESS LOGIC: shouldAutoNavigate
  // ============================================================================
  /**
   * Determines whether auto-navigation should proceed.
   *
   * @returns {boolean} True if auto-navigation is allowed
   */
  function shouldAutoNavigate() {
    // If user has manually clicked a tab or category, permanently disable auto-nav
    if (userHasManuallyInteracted) {
      return false;
    }

    // During simplified mode the My Door tab owns navigation — don't fight it
    if (simplifiedModeActive) {
      return false;
    }

    return true;
  }

  // ============================================================================
  // CORE BUSINESS LOGIC: triggerAutoNavigation
  // ============================================================================
  /**
   * Triggers auto-navigation by publishing navigation request events.
   *
   * PATTERN 3: 5-Stage Event Lifecycle (START -> PROGRESS -> VALIDATION -> SUCCESS/FAILED)
   * PATTERN 10: UI Update Events (ui:tab_open_request, ui:category_open_request)
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {{ tabId: string, categoryId?: string|null, reason: string }} target - Navigation target
   * @param {Object} conductor - The central conductor reference
   * @param {string} trigger - What triggered this navigation (e.g., 'fresh_load', 'restoration', 'selection_applied')
   */
  function triggerAutoNavigation(target, conductor, trigger) {
    const startTime = window.performance
      ? window.performance.now()
      : Date.now();

    try {
      // STAGE 1: START
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_START,
        {
          target,
          trigger,
          timestamp: Date.now(),
        },
        'triggerAutoNavigation'
      );

      // STAGE 2: VALIDATION - verify target is valid
      if (!target || !target.tabId) {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_VALIDATION,
          {
            valid: false,
            errors: ['Invalid navigation target: missing tabId'],
            target,
            trigger,
          },
          'triggerAutoNavigation'
        );

        throw new Error('Invalid navigation target: missing tabId');
      }

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_VALIDATION,
        {
          valid: true,
          target,
          trigger,
        },
        'triggerAutoNavigation'
      );

      // STAGE 3: PROGRESS - publish tab navigation request
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_PROGRESS,
        {
          stage: 'publishing_tab_request',
          tabId: target.tabId,
          trigger,
        },
        'triggerAutoNavigation'
      );

      // PATTERN 10: UI Update Events - request tab opening
      publishEvent(
        CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST,
        {
          tabId: target.tabId,
          source: CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR,
          reason: target.reason,
          trigger,
        },
        'triggerAutoNavigation'
      );

      // If category target exists, request category opening too
      if (target.categoryId) {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_PROGRESS,
          {
            stage: 'publishing_category_request',
            tabId: target.tabId,
            categoryId: target.categoryId,
            trigger,
          },
          'triggerAutoNavigation'
        );

        publishEvent(
          CONSTANTS.EVENTS.UI_CATEGORY_OPEN_REQUEST,
          {
            categoryId: target.categoryId,
            tabId: target.tabId,
            source: CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR,
            reason: target.reason,
            trigger,
          },
          'triggerAutoNavigation'
        );
      }

      // If a gate is the target (an unanswered question card blocking the
      // next real category), scroll to it instead of opening a category --
      // gate cards aren't real categories and don't go through
      // updateCategoryDisplay().
      if (target.gateId) {
        publishEvent(
          CONSTANTS.EVENTS.UI_GATE_OPEN_REQUEST,
          {
            gateId: target.gateId,
            tabId: target.tabId,
            source: CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR,
            reason: target.reason,
            trigger,
          },
          'triggerAutoNavigation'
        );
      }

      // STAGE 4: SUCCESS
      const duration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SUCCESS,
        {
          target,
          trigger,
          duration: parseFloat(duration.toFixed(2)),
          navigatedToTab: target.tabId,
          navigatedToCategory: target.categoryId || null,
        },
        'triggerAutoNavigation'
      );

      // Mark as fired to prevent duplicate navigation
      autoNavigationFired = true;

      // Track total operation performance
      trackPerformance('NAVIGATION_TRIGGER', duration, { target, trigger });

      isDev() &&
        console.log(
          `[AutoNavigationCoordinator] Auto-navigated to tab="${target.tabId}"` +
            (target.categoryId ? ` category="${target.categoryId}"` : '') +
            ` (reason: ${target.reason}, trigger: ${trigger}, ${duration.toFixed(2)}ms)`
        );
    } catch (error) {
      // STAGE 5: FAILED
      const duration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          target,
          trigger,
          duration: parseFloat(duration.toFixed(2)),
        },
        'triggerAutoNavigation'
      );

      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] triggerAutoNavigation failed:',
          error
        );
    }
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles fresh page load (system initialization complete).
   * Navigates to the first incomplete configuration step.
   *
   * @param {Object} conductor - The central conductor
   */
  function handleFreshLoad(conductor) {
    const METHOD_SIGNATURE = 'AutoNavigationCoordinator.handleFreshLoad';
    const startTime = window.performance
      ? window.performance.now()
      : Date.now();

    try {
      isDev() &&
        console.log(
          `[${METHOD_SIGNATURE}] Handling fresh load auto-navigation`
        );

      // Check if auto-navigation should proceed
      if (!shouldAutoNavigate()) {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
          {
            reason: 'user_manually_interacted',
            trigger: 'fresh_load',
            userHasManuallyInteracted,
          },
          METHOD_SIGNATURE
        );

        isDev() &&
          console.log(
            `[${METHOD_SIGNATURE}] Skipped - user has manually interacted`
          );
        return;
      }

      // Check if already fired
      if (autoNavigationFired) {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
          {
            reason: 'already_fired',
            trigger: 'fresh_load',
          },
          METHOD_SIGNATURE
        );

        isDev() && console.log(`[${METHOD_SIGNATURE}] Skipped - already fired`);
        return;
      }

      // Determine navigation target via conductor delegation
      const target = determineTargetNavigation(conductor);

      if (target) {
        triggerAutoNavigation(target, conductor, 'fresh_load');
      } else {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
          {
            reason: 'no_target_determined',
            trigger: 'fresh_load',
          },
          METHOD_SIGNATURE
        );

        isDev() &&
          console.log(`[${METHOD_SIGNATURE}] No navigation target determined`);
      }

      const duration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;
      trackPerformance('TOTAL_OPERATION', duration, { trigger: 'fresh_load' });
    } catch (error) {
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          trigger: 'fresh_load',
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );

      isDev() && console.error(`[${METHOD_SIGNATURE}] Failed:`, error);
    }
  }

  /**
   * PRIVATE HELPER: Handles navigation trigger with common logic.
   * Consolidates duplicated logic from handleRestorationComplete and handleSelectionApplied.
   *
   * @private
   * @param {Object} conductor - The central conductor
   * @param {string} trigger - Trigger source ('restoration' or 'selection_applied')
   */
  function _handleNavigationTrigger(conductor, trigger, categoriesToSkip) {
    categoriesToSkip = categoriesToSkip || new Set();
    const METHOD_SIGNATURE = `AutoNavigationCoordinator._handleNavigationTrigger:${trigger}`;

    try {
      isDev() &&
        console.log(`[${METHOD_SIGNATURE}] Handling auto-navigation trigger`, {
          categoriesToSkip: Array.from(categoriesToSkip),
        });

      // Reset fired flag - allow re-navigation after trigger
      autoNavigationFired = false;

      // Check if auto-navigation should proceed
      if (!shouldAutoNavigate()) {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
          {
            reason: 'user_manually_interacted',
            trigger: trigger,
            userHasManuallyInteracted,
          },
          METHOD_SIGNATURE
        );

        isDev() &&
          console.log(
            `[${METHOD_SIGNATURE}] Skipped - user has manually interacted`
          );
        return;
      }

      // Determine navigation target via conductor delegation, passing categories to skip
      const target = determineTargetNavigation(conductor, categoriesToSkip);

      if (target) {
        triggerAutoNavigation(target, conductor, trigger);
      } else {
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
          {
            reason: 'no_target_determined',
            trigger: trigger,
          },
          METHOD_SIGNATURE
        );
      }
    } catch (error) {
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          trigger: trigger,
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );

      isDev() && console.error(`[${METHOD_SIGNATURE}] Failed:`, error);
    }
  }

  /**
   * Handles URL restoration completion.
   * Re-evaluates navigation after selections are restored from URL params.
   *
   * @param {Object} conductor - The central conductor
   */
  function handleRestorationComplete(conductor) {
    _handleNavigationTrigger(conductor, 'restoration');
  }

  /**
   * Handles post-selection navigation after progress state is fully updated.
   * Triggered by state:progress_updated (fires AFTER completion indexes are rebuilt).
   * After a user applies a selection, navigate to the next incomplete step.
   *
   * CRITICAL: Navigation is DEFERRED to allow cascade auto-selection to claim
   * categories first. state:progress_updated fires BEFORE selection:variant_applied,
   * which means cascade auto-selection hasn't started yet when this fires.
   * We defer by one event loop tick (setTimeout 0) to let the cascade system
   * publish business:cascade_auto_selection_started if applicable.
   * If a cascade is in progress, navigation waits until cascade completes.
   *
   * @param {Object} conductor - The central conductor
   */
  function handleSelectionApplied(conductor) {
    // Bug 1 fix: If an unapply just navigated precisely, suppress this
    // progress_updated-triggered navigation to prevent double-navigation.
    if (suppressProgressNavigation) {
      suppressProgressNavigation = false;
      isDev() &&
        console.log(
          '[AutoNavigationCoordinator] Suppressing progress navigation - unapply already navigated'
        );
      return;
    }

    // Store conductor for deferred use
    deferredConductorRef = conductor;

    // Clear any existing deferred timer
    if (deferredNavigationTimer) {
      clearTimeout(deferredNavigationTimer);
      deferredNavigationTimer = null;
    }

    // If cascade is already in progress, don't navigate now.
    // Navigation will be triggered when cascade completes via handleCascadeCompleted.
    if (cascadeInProgress) {
      isDev() &&
        console.log(
          '[AutoNavigationCoordinator] Deferring navigation - cascade in progress'
        );
      return;
    }

    // Defer navigation by one tick to allow cascade system to start.
    // If selection:variant_applied triggers a cascade, it will set cascadeInProgress=true
    // before this timer fires (synchronous event chain).
    deferredNavigationTimer = setTimeout(() => {
      deferredNavigationTimer = null;

      // Re-check: cascade may have started during the tick
      if (cascadeInProgress) {
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] Deferring navigation - cascade started during tick'
          );
        return;
      }

      _handleNavigationTrigger(conductor, 'selection_applied');
    }, 0);
  }

  /**
   * Handles cascade auto-selection start event.
   * Sets cascadeInProgress flag to defer any pending auto-navigation.
   *
   * PATTERN 4: Event Consumer Handler
   *
   * @param {Object} eventData - Event payload from business:cascade_auto_selection_started
   */
  function handleCascadeStarted(eventData) {
    cascadeInProgress = true;

    // Cancel any pending deferred navigation
    if (deferredNavigationTimer) {
      clearTimeout(deferredNavigationTimer);
      deferredNavigationTimer = null;
    }

    isDev() &&
      console.log(
        '[AutoNavigationCoordinator] Cascade started - deferring auto-navigation',
        { sessionId: eventData?.sessionId }
      );
  }

  /**
   * Handles cascade auto-selection step event.
   * Adds the auto-applied categoryId to the skip set so auto-navigation
   * will skip navigating TO that category.
   *
   * PATTERN 4: Event Consumer Handler
   *
   * @param {Object} eventData - Event payload from business:cascade_auto_selection_step
   */
  function handleCascadeStep(eventData) {
    const categoryId = eventData?.categoryId;
    if (categoryId) {
      cascadeAutoAppliedCategories.add(categoryId);

      isDev() &&
        console.log(
          '[AutoNavigationCoordinator] Cascade auto-applied category - will skip navigation',
          { categoryId }
        );
    }
  }

  /**
   * Handles cascade auto-selection completion event.
   * Clears cascade flag and executes deferred navigation, skipping
   * any categories that were auto-applied during the cascade.
   *
   * PATTERN 4: Event Consumer Handler
   *
   * @param {Object} eventData - Event payload from business:cascade_auto_selection_completed
   */
  function handleCascadeCompleted(eventData) {
    cascadeInProgress = false;

    isDev() &&
      console.log(
        '[AutoNavigationCoordinator] Cascade completed - scheduling deferred navigation',
        {
          autoAppliedCategories: Array.from(cascadeAutoAppliedCategories),
          totalSteps: eventData?.totalSteps,
        }
      );

    // Coalesce onto the SAME deferredNavigationTimer handleSelectionApplied()
    // uses (see that function + its doc comment) -- this used to be a
    // SEPARATE timer (cascadeCompletedCoalesceTimer), which fixed a burst of
    // duplicate BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED events firing back
    // to back but did NOT stop a second, independent STATE_PROGRESS_UPDATED
    // (-> handleSelectionApplied) from ALSO scheduling its own navigation
    // around the same time -- two separate timers, neither aware of the
    // other, both surviving to fire. Found live 2026-08-12: this is why the
    // shelf-mirror/material-preference chain could still produce more than
    // one real navigation to the same or different targets. Sharing one
    // timer variable means whichever call (STATE_PROGRESS_UPDATED or
    // cascade-completed) happens LAST cancels and replaces the other's
    // pending execution -- only one navigation ever survives per burst,
    // regardless of which event(s) contributed to it.
    if (deferredNavigationTimer) {
      clearTimeout(deferredNavigationTimer);
    }
    deferredNavigationTimer = setTimeout(() => {
      deferredNavigationTimer = null;

      // Execute deferred navigation now that cascade is done, passing auto-applied categories to skip
      if (deferredConductorRef) {
        _handleNavigationTrigger(
          deferredConductorRef,
          'selection_applied',
          cascadeAutoAppliedCategories
        );
      }

      // Clear the skip set AFTER navigation has executed
      cascadeAutoAppliedCategories.clear();
    }, 0);
  }

  /**
   * Handles manual user interaction (tab/category click).
   * Disables auto-navigation permanently when user manually navigates.
   *
   * PATTERN 17: XSS Prevention - validates eventData.source is a safe string
   *
   * @param {Object} eventData - Event data from ui:tab_opened or ui:category_opened
   */
  function handleManualInteraction(eventData) {
    try {
      // Only disable auto-nav for MANUAL interactions (not auto-navigator events)
      const source =
        typeof eventData?.source === 'string' ? eventData.source : '';

      if (source === CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR) {
        // This was triggered by us, ignore
        return;
      }

      // User manually clicked a tab or category - disable auto-nav permanently
      userHasManuallyInteracted = true;

      isDev() &&
        console.log(
          '[AutoNavigationCoordinator] User manual interaction detected - auto-navigation disabled',
          {
            source: source || 'unknown',
            eventType: eventData?.eventType || 'unknown',
          }
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] handleManualInteraction failed:',
          error
        );
    }
  }

  /**
   * CONFIRM AND CONTINUE — "I'm happy with this answer, what's next?"
   *
   * Published by md-config-core-event-handler.js when the customer re-taps the
   * tile that is ALREADY applied, in apo_style + click-apply. That gesture used
   * to re-apply the identical selection and therefore do nothing visible; it
   * now asks this coordinator, which already owns "where should the customer be
   * next", to answer it.
   *
   * 🚨 THIS IS NOT A NAVIGATION, AND EVERY LINE OF IT IS SHAPED BY THAT.
   *
   *  - It does NOT touch `userHasManuallyInteracted`. Only
   *    handleManualInteraction sets that flag, from UI_TAB_OPENED /
   *    UI_CATEGORY_OPENED, and it is permanent for the session. A customer
   *    confirming a choice must not cost them auto-navigation for the rest of
   *    their visit.
   *  - It publishes neither UI_CATEGORY_OPENED nor UI_TAB_OPENED, for the same
   *    reason -- publishing either would trip that very flag through the front
   *    door.
   *  - It does not call _handleNavigationTrigger, set autoNavigationFired, or
   *    touch any suppression flag. It reads determineTargetNavigation and
   *    publishes a UI intent; the auto-navigation state machine is untouched.
   *
   * Silent during a cascade: a cascade is mid-flight state, so
   * determineTargetNavigation would answer against a door that is still being
   * assembled -- the same reason every other handler here defers on
   * `cascadeInProgress`. The customer's own tap is not lost in practice, since
   * a cascade cannot be running at the moment they tap an already-settled tile;
   * and if one somehow is, doing nothing beats sending them to a card that is
   * about to be auto-answered.
   *
   * @param {Object} eventData - { categoryId, variantId?, source }
   * @param {Object} conductor - The central conductor
   */
  function handleConfirmAndContinue(eventData, conductor) {
    try {
      if (!conductor) return false;

      // apo_style only. The publisher already gates on it, but this event is a
      // public bus name and classic/dual have a real APPLY button and a real
      // Summary to navigate to -- a stray publish there must not start moving
      // their page around.
      if (conductor._getConfiguratorExperience?.() !== 'apo_style') {
        return false;
      }

      if (cascadeInProgress) {
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] confirm-and-continue ignored - cascade in progress'
          );
        return false;
      }

      const categoryId =
        typeof eventData?.categoryId === 'string' ? eventData.categoryId : null;

      // Skip the category just confirmed. It is complete by definition (it
      // holds an applied answer), so determineTargetNavigation would skip it
      // anyway -- this makes "never send them back where they already are"
      // structural rather than a consequence of the completion check.
      const skip = new Set();
      if (categoryId) skip.add(categoryId);

      const target = determineTargetNavigation(conductor, skip);

      if (target && (target.categoryId || target.gateId)) {
        publishEvent(
          CONSTANTS.EVENTS.UI_CATEGORY_FOCUS_REQUESTED,
          {
            categoryId: target.categoryId || null,
            gateId: target.gateId || null,
            source: CONSTANTS.NAV_TAP_SOURCES.CONFIRM_CONTINUE,
            reason: target.reason || null,
          },
          'handleConfirmAndContinue'
        );
        return true;
      }

      if (target) {
        // A target with neither a categoryId nor a gateId is a TAB-shaped
        // answer from the legacy branch of determineTargetNavigation. apo_style
        // has no tabs to go to, and the door is NOT finished, so pointing at
        // Add to Cart here would be a lie. Do nothing.
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] confirm-and-continue: target has no card',
            { tabId: target.tabId, reason: target.reason }
          );
        return false;
      }

      // null means "there is nowhere left to go": either nothing incomplete
      // remains, or apo_style's explicit 100%-complete branch fired (which
      // returns null precisely BECAUSE there is no summary to navigate to in
      // this mode -- the Add to Cart button at the end of the rail is the
      // destination). Both are the same answer to the customer's question.
      publishEvent(
        CONSTANTS.EVENTS.UI_BUY_AREA_FOCUS_REQUESTED,
        { source: CONSTANTS.NAV_TAP_SOURCES.CONFIRM_CONTINUE },
        'handleConfirmAndContinue'
      );
      return true;
    } catch (error) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] handleConfirmAndContinue failed:',
          error
        );
      return false;
    }
  }

  /**
   * Handles selection unapply completion.
   * Navigates immediately to the unapplied category and suppresses the
   * subsequent state:progress_updated navigation to prevent double-navigation.
   *
   * PATTERN 4: Event Consumer Handler
   *
   * @param {Object} eventData - Event payload from selection:user_unapply_completed
   * @param {Object} conductor - The central conductor
   */
  function handleUnapplyCompleted(eventData, conductor) {
    try {
      const categoryId = eventData?.categoryId;
      if (!categoryId) {
        isDev() &&
          console.warn(
            '[AutoNavigationCoordinator] SELECTION_USER_UNAPPLY_COMPLETED received without categoryId, ignoring'
          );
        return;
      }

      // Check suppression: user has manually interacted
      if (!shouldAutoNavigate()) {
        isDev() &&
          console.log(
            '[AutoNavigationCoordinator] Unapply navigation skipped - user has manually interacted'
          );
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
          {
            reason: 'user_manually_interacted',
            trigger: 'unapply_completed',
            categoryId,
            userHasManuallyInteracted,
          },
          'handleUnapplyCompleted'
        );
        return;
      }

      // Look up the parent tab for this category via conductor delegation
      const tabId = conductor.getParentTabForCategory
        ? conductor.getParentTabForCategory(categoryId)
        : null;

      if (!tabId) {
        isDev() &&
          console.warn(
            '[AutoNavigationCoordinator] Could not determine parent tab for unapplied category:',
            categoryId
          );
        return;
      }

      isDev() &&
        console.log(
          '[AutoNavigationCoordinator] Unapply completed - navigating to unapplied category',
          { categoryId, tabId }
        );

      // Reset fired flag to allow navigation
      autoNavigationFired = false;

      // Suppress the next state:progress_updated navigation to prevent
      // double-navigation race condition (Bug 1 fix)
      suppressProgressNavigation = true;

      // Safety net: clear the flag after 1s in case state:progress_updated never fires
      // (e.g., no incomplete categories remain after unapply). Prevents the flag from
      // leaking into unrelated future selection flows.
      setTimeout(function () {
        suppressProgressNavigation = false;
      }, 1000);

      // Build a frozen target and trigger navigation via the standard 5-stage lifecycle
      const target = Object.freeze({
        tabId,
        categoryId,
        reason: 'unapply_completed',
        totalProgress: null,
      });

      triggerAutoNavigation(target, conductor, 'unapply_completed');
    } catch (error) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] handleUnapplyCompleted failed:',
          error
        );
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          trigger: 'unapply_completed',
          source: 'AutoNavigationCoordinator.handleUnapplyCompleted',
        },
        'handleUnapplyCompleted'
      );
    }
  }

  /**
   * 🎯 GOLDEN STANDARD: Handles disabled Add to Cart button clicks
   *
   * CCP COMPLIANCE - Business Logic Responsibility:
   * - Finds incomplete tab/category (business logic - conductor delegation)
   * - Triggers navigation via existing triggerAutoNavigation() (event-driven)
   * - Does NOT touch DOM (that's UIUpdater's job!)
   *
   * PATTERN: Business logic determines WHERE, UI module handles HOW (animations)
   *
   * @param {Object} eventData - Event payload from UI_ADD_TO_CART_DISABLED_CLICKED
   */
  function handleDisabledCartButtonClick(eventData) {
    const METHOD_SIGNATURE =
      'AutoNavigationCoordinator.handleDisabledCartButtonClick';

    try {
      isDev() &&
        console.log(
          `[${METHOD_SIGNATURE}] Disabled Add to Cart clicked - finding navigation target`
        );

      // Get conductor reference
      const conductor = window.MurphyDoorConfigurator?.conductor;
      if (!conductor) {
        isDev() &&
          console.warn(`[${METHOD_SIGNATURE}] Conductor not available`);
        return;
      }

      // Business logic: determine navigation target via conductor delegation (PPD-first)
      const target = determineTargetNavigation(conductor);
      if (!target) {
        isDev() &&
          console.log(`[${METHOD_SIGNATURE}] No navigation target determined`);
        return;
      }

      // Ryan, UAT 2026-08-27: "it should auto-scroll them to the category
      // button that is still pending a choice, with it highlighted in the
      // warning border color."
      //
      // The highlight is published, not applied. This is a bl- module and CCP
      // rule 4 reserves DOM writes for md-config-ui- modules, so the request
      // goes to ui-updater, which owns the class. Published BEFORE the
      // navigation delay so the card is already marked when it scrolls into
      // view rather than lighting up a beat afterwards.
      publishEvent(
        CONSTANTS.EVENTS.UI_PENDING_CATEGORY_CALLOUT,
        {
          categoryId: target.categoryId || null,
          gateId: target.gateId || null,
          reason: target.reason || null,
          timestamp: Date.now(),
        },
        METHOD_SIGNATURE
      );

      // ✅ CCP COMPLIANT: Trigger auto-navigation with delay (DRY: reuse existing infrastructure)
      setTimeout(() => {
        isDev() &&
          console.log(
            '[DEBUG] setTimeout fired! About to call triggerAutoNavigation'
          );
        triggerAutoNavigation(
          target,
          conductor,
          'disabled_cart_button_clicked'
        );
      }, 1500);

      isDev() &&
        console.log(
          `[${METHOD_SIGNATURE}] Navigation scheduled for tab="${target.tabId}", category="${target.categoryId}"`
        );
    } catch (error) {
      isDev() && console.error(`[${METHOD_SIGNATURE}] Failed:`, error);
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          trigger: 'disabled_cart_button_clicked',
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    }
  }

  // ============================================================================
  // MODULE INITIALIZATION
  // ============================================================================
  /**
   * Initialize the auto-navigation coordinator.
   *
   * PATTERN 4: Event Consumer Handlers - subscribes to all required events
   * PATTERN 16: Initialization Guard - prevents double-init
   * PATTERN 15: Cleanup Arrays - tracks all subscriptions for teardown
   *
   * @param {Object} conductor - The central conductor for delegation
   * @returns {boolean} True if initialization succeeded
   */
  function initialize(conductor) {
    // PATTERN 16: Initialization Guard
    if (isInitialized) {
      isDev() &&
        console.warn(
          '[AutoNavigationCoordinator] Already initialized, skipping'
        );
      return false;
    }

    if (!conductor) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] Cannot initialize: conductor is required'
        );
      return false;
    }

    const bus = eventBus || window.MurphyDoorConfigurator?.CoreEventBus;
    if (!bus) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] Cannot initialize: CoreEventBus not available'
        );
      return false;
    }

    eventBus = bus;

    try {
      // PATTERN 4: Event Consumer Handlers
      // Subscribe to system initialization for fresh loads
      const unsubInit = bus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_INITIALIZATION_COMPLETE,
        () => handleFreshLoad(conductor)
      );
      cleanupHandlers.push(unsubInit);

      // Subscribe to restoration complete for URL param restoration
      const unsubRestore = bus.subscribe(
        CONSTANTS.EVENTS.RESTORATION_COMPLETE,
        () => handleRestorationComplete(conductor)
      );
      cleanupHandlers.push(unsubRestore);

      // Subscribe to state:progress_updated for post-selection navigation
      // CRITICAL: We listen to state:progress_updated instead of selection:applied because
      // selection:applied fires from handleSelectionState() BEFORE the progress tracker and
      // tab completion indexes are updated. state:progress_updated fires AFTER:
      // 1. progressTracker.updateCategoryProgress() marks the category complete
      // 2. handleStateChange('COMPLETION_UPDATE') updates STATE_MAPS completion
      // 3. _updateTabCompletionIndex() rebuilds the O(1) index with fresh data
      // This ensures findFirstIncompleteCategory() returns the NEXT incomplete category,
      // not the one that was just applied.
      const unsubSelection = bus.subscribe(
        CONSTANTS.EVENTS.STATE_PROGRESS_UPDATED,
        () => handleSelectionApplied(conductor)
      );
      cleanupHandlers.push(unsubSelection);

      // Gate answers that reveal ONLY already-visible categories (e.g.
      // Cabinets? -> "Top and Bottom", where BOTH Top and Bottom Cabinet
      // Doors were already visible and neither needs a "no doors" fallback
      // applied) never trigger a cascade or any real category completion --
      // gate-card-coordinator.js's handleGateAnswerChanged early-returns with
      // selections.length === 0, so BUSINESS_CASCADE_APPLY_GATE_DEFAULTS
      // never publishes and STATE_PROGRESS_UPDATED above never fires either.
      // Auto-nav had nothing to react to, so it silently never advanced past
      // the gate -- confirmed live 2026-08-13 (Daniel): only "Top and Bottom"
      // got stuck; "Top Only"/"Bottom Only" worked because the OTHER side's
      // fallback apply DOES produce a real STATE_PROGRESS_UPDATED. Reusing
      // handleSelectionApplied() (not a bespoke handler) is deliberate -- its
      // existing one-tick-defer + cascadeInProgress re-check already handles
      // the case where THIS gate answer's OWN cascade fires (Top/Bottom
      // Only), so hooking the same function here can't double-navigate or
      // race whatever cascade a different answer does trigger.
      const unsubGateAnswerNav = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED,
        () => handleSelectionApplied(conductor)
      );
      cleanupHandlers.push(unsubGateAnswerNav);

      // Confirm-and-continue: the customer re-tapped an already-applied tile.
      // See handleConfirmAndContinue for why this is deliberately NOT wired
      // through the manual-interaction / auto-navigation machinery below.
      const unsubConfirmContinue = bus.subscribe(
        CONSTANTS.EVENTS.UI_CONFIRM_AND_CONTINUE_REQUESTED,
        (eventData) => handleConfirmAndContinue(eventData, conductor)
      );
      cleanupHandlers.push(unsubConfirmContinue);

      // Subscribe to manual interaction events (tab/category user clicks)
      const unsubTabOpened = bus.subscribe(
        CONSTANTS.EVENTS.UI_TAB_OPENED,
        (eventData) => handleManualInteraction(eventData)
      );
      cleanupHandlers.push(unsubTabOpened);

      const unsubCategoryOpened = bus.subscribe(
        CONSTANTS.EVENTS.UI_CATEGORY_OPENED,
        (eventData) => handleManualInteraction(eventData)
      );
      cleanupHandlers.push(unsubCategoryOpened);

      // Subscribe to cascade auto-selection lifecycle events
      // When cascade is in progress, auto-navigation DEFERS until cascade completes.
      // This prevents navigating TO a category that is about to be auto-applied.
      const unsubCascadeStarted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
        (eventData) => handleCascadeStarted(eventData)
      );
      cleanupHandlers.push(unsubCascadeStarted);

      const unsubCascadeStep = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
        (eventData) => handleCascadeStep(eventData)
      );
      cleanupHandlers.push(unsubCascadeStep);

      const unsubCascadeCompleted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        (eventData) => handleCascadeCompleted(eventData)
      );
      cleanupHandlers.push(unsubCascadeCompleted);

      // Subscribe to selection unapply completion for immediate navigation to the
      // newly-incomplete category. This is more precise than waiting for
      // state:progress_updated which fires asynchronously and creates a race window.
      const unsubUnapplyCompleted = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
        (eventData) => handleUnapplyCompleted(eventData, conductor)
      );
      cleanupHandlers.push(unsubUnapplyCompleted);

      // Bug 2 fix: Cancel deferred navigation when an unapply is REQUESTED
      // (same defensive pattern as handleCascadeStarted cancelling pending timers)
      const unsubUnapplyRequested = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
        (eventData) => {
          // Cancel any pending deferred navigation timer
          if (deferredNavigationTimer) {
            clearTimeout(deferredNavigationTimer);
            deferredNavigationTimer = null;
            isDev() &&
              console.log(
                '[AutoNavigationCoordinator] Cancelled deferred navigation - unapply requested',
                { categoryId: eventData?.categoryId }
              );
          }

          // Reset stale suppression flag so it does not leak across unrelated flows
          suppressProgressNavigation = false;
        }
      );
      cleanupHandlers.push(unsubUnapplyRequested);

      // Bug 3 fix: Monitor unapply failures for event observability
      const unsubUnapplyFailed = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_FAILED,
        (eventData) => {
          isDev() &&
            console.warn(
              '[AutoNavigationCoordinator] Unapply failed - skipping navigation',
              { categoryId: eventData?.categoryId, error: eventData?.error }
            );

          publishEvent(
            CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_SKIPPED,
            {
              reason: 'unapply_failed',
              trigger: 'unapply_failed',
              categoryId: eventData?.categoryId || null,
              error: eventData?.error || 'unknown',
            },
            'handleUnapplyFailed'
          );
        }
      );
      cleanupHandlers.push(unsubUnapplyFailed);

      // NOTE: UI_ADD_TO_CART_DISABLED_CLICKED subscription moved to MODULE LOAD TIME
      // (see bootstrap subscription block above) to fix publish-before-subscribe race condition.
      // UIUpdater attaches click handler EARLY (on Summary tab activation), but initialize()
      // runs LATE. Moving to load time ensures subscriber exists when event fires.

      // Subscribe to implicit apply lifecycle events
      // When implicit apply starts, suppress progress navigation to prevent double-navigation
      // (the implicit apply coordinator will navigate after apply completes)
      const unsubImplicitApplyStarted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_STARTED,
        () => {
          suppressProgressNavigation = true;
          isDev() &&
            console.log(
              '[AutoNavigationCoordinator] suppressProgressNavigation set by implicit apply'
            );
          // Safety net: clear the flag after 2s in case the implicit apply flow aborts
          // and neither COMPLETED nor FAILED events fire (prevents silent auto-nav lockout).
          setTimeout(() => {
            if (suppressProgressNavigation) {
              suppressProgressNavigation = false;
              isDev() &&
                console.warn(
                  '[AutoNavigationCoordinator] suppressProgressNavigation cleared by safety timer (implicit apply did not complete)'
                );
            }
          }, 2000);
        }
      );
      cleanupHandlers.push(unsubImplicitApplyStarted);

      // Reset suppression flag when implicit apply completes (navigation already handled)
      const unsubImplicitApplyCompleted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COMPLETED,
        () => {
          suppressProgressNavigation = false;
          isDev() &&
            console.log(
              '[AutoNavigationCoordinator] suppressProgressNavigation cleared by implicit apply completion'
            );
        }
      );
      cleanupHandlers.push(unsubImplicitApplyCompleted);

      // Also reset on implicit apply failure to prevent flag leaking
      const unsubImplicitApplyFailed = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_FAILED,
        () => {
          suppressProgressNavigation = false;
          isDev() &&
            console.log(
              '[AutoNavigationCoordinator] suppressProgressNavigation cleared by implicit apply failure'
            );
        }
      );
      cleanupHandlers.push(unsubImplicitApplyFailed);

      // Mark as initialized
      isInitialized = true;

      // Publish module ready event
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_COORDINATOR_READY,
        {
          module: 'AutoNavigationCoordinator',
          subscriptionCount: cleanupHandlers.length,
          timestamp: Date.now(),
        },
        'initialize'
      );

      isDev() &&
        console.log(
          `[AutoNavigationCoordinator] Initialized with ${cleanupHandlers.length} event subscriptions`
        );

      return true;
    } catch (error) {
      isDev() &&
        console.error(
          '[AutoNavigationCoordinator] Initialization failed:',
          error
        );

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          source: 'AutoNavigationCoordinator.initialize',
        },
        'initialize'
      );

      return false;
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset module state.
   *
   * PATTERN 15: Cleanup Arrays - unsubscribes all tracked handlers
   */
  function cleanup() {
    isDev() &&
      console.log(
        `[AutoNavigationCoordinator] Cleaning up ${cleanupHandlers.length} subscriptions`
      );

    // Unsubscribe all event handlers
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (error) {
        isDev() &&
          console.error(
            '[AutoNavigationCoordinator] Cleanup handler error:',
            error
          );
      }
    });
    cleanupHandlers = [];

    // Clear deferred navigation timer
    if (deferredNavigationTimer) {
      clearTimeout(deferredNavigationTimer);
      deferredNavigationTimer = null;
    }

    // Reset state
    isInitialized = false;
    userHasManuallyInteracted = false;
    autoNavigationFired = false;
    suppressProgressNavigation = false;
    cascadeInProgress = false;
    cascadeAutoAppliedCategories.clear();
    deferredConductorRef = null;

    // Publish cleanup event
    publishEvent(
      CONSTANTS.EVENTS.BUSINESS_AUTO_NAVIGATION_COORDINATOR_CLEANUP,
      {
        module: 'AutoNavigationCoordinator',
        timestamp: Date.now(),
      },
      'cleanup'
    );
  }

  // ============================================================================
  // PUBLIC API (Exposed on namespace)
  // ============================================================================
  window.MurphyDoorConfigurator.autoNavigationCoordinator = {
    /**
     * Initialize the coordinator with conductor reference.
     * @param {Object} conductor - The central conductor
     * @returns {boolean} Success
     */
    initialize,

    /**
     * Cleanup all subscriptions and reset state.
     */
    cleanup,

    /**
     * Check if auto-navigation is currently enabled.
     * @returns {boolean}
     */
    isAutoNavigationEnabled: function () {
      return shouldAutoNavigate();
    },

    /**
     * Get current module state for debugging.
     * PATTERN 18: Returns frozen state object.
     * @returns {Object} Current module state (frozen)
     */
    getState: function () {
      return Object.freeze({
        isInitialized,
        bootstrapComplete,
        userHasManuallyInteracted,
        autoNavigationFired,
        suppressProgressNavigation,
        cascadeInProgress,
        cascadeAutoAppliedCategories: Array.from(cascadeAutoAppliedCategories),
        hasDeferredNavigation: deferredNavigationTimer !== null,
        subscriptionCount: cleanupHandlers.length,
        pendingCallCount: pendingCalls.length,
      });
    },

    // Expose PERFORMANCE_THRESHOLDS for external reference/testing
    PERFORMANCE_THRESHOLDS,

    // Exposed for external reference/testing (see gate-target/tab-scan
    // precedent above) -- not part of the event-driven public contract,
    // callers should publish BUSINESS_AUTO_NAVIGATION_REQUESTED instead.
    determineTargetNavigation,

    // Same caveat: exposed so the confirm-and-continue target selection can be
    // unit-tested directly. Callers publish UI_CONFIRM_AND_CONTINUE_REQUESTED.
    handleConfirmAndContinue,
  };

  isDev() && console.log('[AutoNavigationCoordinator] Module loaded');
})(window);
